import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getCurrentUser } from "../services/api";
import { ModuleType } from "../types/modules";
import { getDomainConfig } from "../utils/domainConfig";
import type { AuthMeLegal } from "../services/legalApi";

interface Hotel {
  id: string;
  name: string;
  subdomain: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  profitroomCode?: string;
  profitroomDefaultOfferId?: string;
}

interface Brand {
  id: string;
  slug: string;
  displayName: string;
  description: string | null;
  primaryColor: string;
  logoUrl: string | null;
  sidebarLogoUrl: string | null;
  replyToAddress?: string | null;
}

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  enabledModules: string[];
  role: string;
  title?: string;
  description?: string;
  brand?: Brand;
  customDomains?: string[];
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  hotels?: Hotel[];
  hasPassword?: boolean;
  defaultHotel?: {
    id: string;
    customDomain: string;
    name: string;
  } | null;
  tenants?: Tenant[];
  allEnabledModules?: string[];
  impersonatorId?: string | null;
  /** Re-acceptance status for the soft legal consent modal (spec 2026-07-03). */
  legal?: AuthMeLegal;
}

interface HotelContextType {
  user: User | null;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  currentHost: string;
  setMockHost: (host: string) => void;
  hasModuleEnabled: (module: ModuleType) => boolean;
  getTenantDisplayInfo: () => { title: string; description: string };
  refreshUser: () => Promise<void>;
  selectedTenantId: string | null;
  setSelectedTenant: (tenantId: string) => void;
  currentTenant: Tenant | null;
  setToken: (token: string) => void;
  isImpersonating: boolean;
}

const HotelContext = createContext<HotelContextType | undefined>(undefined);

export const HotelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(
    null
  );

  // Wrapper to persist to localStorage and update state
  const setSelectedTenant = useCallback((tenantId: string) => {
    localStorage.setItem("selectedTenantId", tenantId);
    setSelectedTenantIdState(tenantId);
  }, []);

  // Derive current tenant from selectedTenantId
  const currentTenant =
    user?.tenants?.find((t) => t.id === selectedTenantId) || null;

  // Derived impersonation state
  const isImpersonating = !!user?.impersonatorId;

  // Set token in localStorage (used for impersonation)
  const setToken = useCallback((token: string) => {
    localStorage.setItem("token", token);
  }, []);

  const getCurrentHost = () => {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return localStorage.getItem("mockHost") || "localhost";
    }
    return hostname;
  };

  const setMockHost = (host: string) => {
    localStorage.setItem("mockHost", host);
    // Reload to apply new host
    window.location.reload();
  };

  const hasModuleEnabled = (module: ModuleType): boolean => {
    // Validate module format
    if (!module.includes(':')) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[hasModuleEnabled] Invalid module format: "${module}". Modules must contain ":" (e.g., "grants:calls", "admin:kiedynabory:read")`);
      }
      return false;
    }

    if (!user?.allEnabledModules) return false;

    // Check exact match
    if (user.allEnabledModules.includes(module)) return true;

    // Wildcard matching algorithm:
    // 1. Check exact match (done above)
    // 2. Check if enabled module is a wildcard that covers the required module
    //    e.g., 'admin:*' covers 'admin:kiedynabory:calls'
    //    e.g., 'admin:kiedynabory:*' covers 'admin:kiedynabory:calls'
    // Build wildcards at each level by progressively removing the rightmost segment
    const moduleParts = module.split(":");
    for (let i = moduleParts.length - 1; i >= 1; i--) {
      const prefix = moduleParts.slice(0, i).join(":");
      const wildcardModule = `${prefix}:*`;
      if (user.allEnabledModules.includes(wildcardModule)) {
        return true;
      }
    }

    return false;
  };

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const userData = await getCurrentUser();

      // If user has a default hotel and no mockHost is set (or it's the default), update it
      if (userData.defaultHotel) {
        const currentMockHost = localStorage.getItem("mockHost");
        if (!currentMockHost || currentMockHost === "demo.kiedynabory.pl") {
          localStorage.setItem("mockHost", userData.defaultHotel.customDomain);
        }
      }

      setUser({
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role,
        hotels: userData.hotels,
        hasPassword: userData.hasPassword,
        defaultHotel: userData.defaultHotel,
        tenants: userData.tenants,
        allEnabledModules: userData.allEnabledModules,
        impersonatorId: userData.impersonatorId,
        legal: userData.legal,
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Error fetching user data:", error);
      }
      // If token is invalid, remove it
      localStorage.removeItem("token");
      setUser(null);
    }
  }, []);

  // Initialize selected tenant from localStorage or user's first tenant
  useEffect(() => {
    if (user?.tenants && user.tenants.length > 0) {
      // Try to get from localStorage
      const stored = localStorage.getItem("selectedTenantId");

      // Validate stored ID against user's accessible tenants
      if (stored) {
        const isValid = user.tenants.some((t) => t.id === stored);
        if (isValid) {
          setSelectedTenantIdState(stored);
          return;
        }
        // Clear invalid selection
        localStorage.removeItem("selectedTenantId");
      }

      // Fallback to first tenant
      const firstTenantId = user.tenants[0].id;
      setSelectedTenantIdState(firstTenantId);
      localStorage.setItem("selectedTenantId", firstTenantId);

      // This /auth/me was answered without the selection (or with a stale
      // one), and `legal` depends on the selected tenant's brand (ADR-088).
      if (user.tenants.length > 1) {
        void refreshUser();
      }
    }
  }, [user, refreshUser]);

  const getTenantDisplayInfo = useCallback((): {
    title: string;
    description: string;
  } => {
    const currentHost = getCurrentHost();
    const domainConfig = getDomainConfig();

    // If user has no tenants, use domain-based fallback
    if (!user?.tenants || user.tenants.length === 0) {
      return { title: domainConfig.brandName, description: domainConfig.brandDescription };
    }

    // If user has only one tenant, use it automatically
    if (user.tenants.length === 1) {
      const tenant = user.tenants[0];
      return {
        title:
          tenant.brand?.displayName ||
          tenant.title ||
          tenant.name ||
          domainConfig.brandName,
        description: tenant.brand?.description || tenant.description || "",
      };
    }

    // Find tenant based on current host (for multi-tenant users)
    const currentTenant = user.tenants.find((tenant) => {
      // Check if subdomain matches
      if (currentHost.includes(tenant.subdomain)) {
        return true;
      }

      // Check custom domains if they exist
      // Note: customDomains might not be available in current response, but logic is here for future
      if (tenant.customDomains) {
        return tenant.customDomains.some(
          (domain) =>
            currentHost.includes(domain) || domain.includes(currentHost)
        );
      }

      return false;
    });

    if (currentTenant) {
      return {
        title:
          currentTenant.brand?.displayName ||
          currentTenant.title ||
          currentTenant.name ||
          domainConfig.brandName,
        description:
          currentTenant.brand?.description || currentTenant.description || "",
      };
    }

    // Fallback to first tenant if no mapping found
    const firstTenant = user.tenants[0];
    return {
      title:
        firstTenant.brand?.displayName ||
        firstTenant.title ||
        firstTenant.name ||
        domainConfig.brandName,
      description:
        firstTenant.brand?.description || firstTenant.description || "",
    };
  }, [user]);

  useEffect(() => {
    const initializeContext = async () => {
      // Fetch user data if token exists
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const userData = await getCurrentUser();

          // If user has a default hotel and no mockHost is set (or it's the default), update it
          if (userData.defaultHotel) {
            const currentMockHost = localStorage.getItem("mockHost");
            if (!currentMockHost || currentMockHost === "demo.hotelb2b.com") {
              localStorage.setItem(
                "mockHost",
                userData.defaultHotel.customDomain
              );
            }
          }

          setUser({
            id: userData.id,
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            role: userData.role,
            hotels: userData.hotels,
            hasPassword: userData.hasPassword,
            defaultHotel: userData.defaultHotel,
            tenants: userData.tenants,
            allEnabledModules: userData.allEnabledModules,
            impersonatorId: userData.impersonatorId,
            legal: userData.legal,
          });
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.error("Error fetching user data:", error);
          }
          // If token is invalid, remove it
          localStorage.removeItem("token");
        }
      }

      setIsLoading(false);
    };

    initializeContext();
  }, []);

  // Update document title based on tenant info
  useEffect(() => {
    if (!isLoading) {
      const { title, description } = getTenantDisplayInfo();
      const domainConfig = getDomainConfig();

      let documentTitle = `${domainConfig.brandName} - ${domainConfig.brandDescription}`; // Default fallback

      if (title && description) {
        documentTitle = `${title} - ${description}`;
      } else if (title) {
        documentTitle = title;
      }

      document.title = documentTitle;
    }
  }, [user, isLoading, getTenantDisplayInfo]);

  return (
    <HotelContext.Provider
      value={{
        user,
        isLoading,
        setUser,
        currentHost: getCurrentHost(),
        setMockHost,
        hasModuleEnabled,
        getTenantDisplayInfo,
        refreshUser,
        selectedTenantId,
        setSelectedTenant,
        currentTenant,
        setToken,
        isImpersonating,
      }}
    >
      {children}
    </HotelContext.Provider>
  );
};

export const useHotel = () => {
  const context = useContext(HotelContext);
  if (context === undefined) {
    throw new Error("useHotel must be used within a HotelProvider");
  }
  return context;
};
